import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { DRAWER_DATA, DRAWER_REF, DrawerRef } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';
import { CIPXDrawerContentInterface } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-content.interface';
import { ShoppingCartDrawerData } from '@sbd-doug-frontend/shared/lib/shopping-cart/shopping-cart-drawer.types';
import { CIPXShoppingCartService } from '@sbd-doug-frontend/shared/lib/shopping-cart/shopping-cart.service';
import { CIPXShoppingCartComponent } from '@sbd-doug-frontend/shared/lib/shopping-cart/shopping-cart.component';
import { ShoppingCartStep } from '@sbd-doug-frontend/shared/lib/shopping-cart/shopping-cart-steps.enum';

@Component({
  selector: 'app-shopping-cart-drawer',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatListModule, CIPXShoppingCartComponent],
  template: `
    <cipx-shopping-cart
      #shoppingCartComponent
      (stepChanged)="onStepChanged($event)"
      (closeDrawer)="closeDrawer()"
      (overlaySettingsChanged)="onOverlaySettingsChanged($event)">
    </cipx-shopping-cart>
  `,
  styles: [
    `
      // Quote request drawer wrapper styles
      @use '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-content.scss' as drawer;

      :host {
        @include drawer.drawer-content-host;
      }

      // Ensure the intake form component gets full height so we can scroll the content
      // and have the footer sticky.
      cipx-shopping-cart {
        display: block;
        height: 100%;
      }
    `,
  ],
})
export class ShoppingCartDrawerComponent implements CIPXDrawerContentInterface<ShoppingCartDrawerData>, OnInit {
  private _shoppingCartService = inject(CIPXShoppingCartService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  @Output() closeRequested = new EventEmitter<void>();

  @ViewChild('shoppingCartComponent') shoppingCartComponent!: CIPXShoppingCartComponent;

  private _closeEventEmitted = false;
  private _currentStep: ShoppingCartStep = ShoppingCartStep.CART;

  // Inject typed data
  data = inject(DRAWER_DATA, { optional: true }) as ShoppingCartDrawerData | null;

  // Inject drawer ref
  drawerRef = inject(DRAWER_REF, { optional: true }) as DrawerRef<ShoppingCartDrawerComponent> | null;

  ngOnInit(): void {
    // Initial drawer setup - will be updated by step changes
    this.drawerRef?.setTitle('Shopping Cart');
    this.drawerRef?.setShowBackButton(false);
  }

  onStepChanged(stepInfo: {
    step: ShoppingCartStep;
    title: string;
    showBackButton: boolean;
    canGoBack: boolean;
  }) {
    this._currentStep = stepInfo.step;

    // Update drawer title and back button
    this.drawerRef?.setTitle(stepInfo.title);

    // Disable back button for CONFIRMATION step
    const shouldShowBackButton = stepInfo.step === ShoppingCartStep.CONFIRMATION ? false : stepInfo.showBackButton;
    this.drawerRef?.setShowBackButton(shouldShowBackButton);

    // console.log(`Shopping cart step changed to: ${ShoppingCartStep[stepInfo.step]} - ${stepInfo.title}`);
  }

    onOverlaySettingsChanged(settings: { transparentOverlay: boolean; disableOverlay: boolean }) {
    // Update drawer overlay settings
    // console.log(`Attempting to change overlay settings:`, settings);
    // console.log(`DrawerRef available:`, !!this.drawerRef);

    if (this.drawerRef) {
      this.drawerRef.setTransparentOverlay(settings.transparentOverlay);
      this.drawerRef.setDisableOverlay(settings.disableOverlay);
      // console.log(`Overlay settings applied successfully`);
    } else {
      // console.warn(`DrawerRef not available - cannot update overlay settings`);
    }
  }

  removeItem(item: any) {
    this._shoppingCartService.removeItem(item);
    // Update local data
    if (this.data?.items) {
      this.data.items = this.data.items.filter((i) => i.id !== item.id);
    }
  }

  checkout() {
    // Implement checkout logic specific to account-portal
    // console.log('Account Portal checkout logic here');
    this.closeDrawer();
  }

  closeDrawer() {
    if (!this._closeEventEmitted) {
      this._closeEventEmitted = true;
      // Clear the shopping cart when drawer is closed
      this._shoppingCartService.clear();
      this.closeRequested.emit();
    }
  }

  // Implement CIPXDrawerContentInterface methods
  onBackClick() {
    // Handle back navigation based on current step
    if (this.shoppingCartComponent) {
      this.shoppingCartComponent.handleDrawerBackClick();
    }
  }

  canClose(): boolean {
    return true;
  }

  afterDrawerOpened(): void {
    // Initialize any data or focus elements if needed
    // Reset close event flag for new drawer instance
    this._closeEventEmitted = false;
  }

  beforeDrawerClosed(): void {
    // Clean up any subscriptions or temporary data
    // Reset overlay settings to default
    this.drawerRef?.setTransparentOverlay(true);
    this.drawerRef?.setDisableOverlay(true);

    // Also emit closeRequested to notify wrapper service (if not already emitted)
    if (!this._closeEventEmitted) {
      this._closeEventEmitted = true;
      // Clear the shopping cart when drawer is closed externally (X button, ESC, etc.)
      this._shoppingCartService.clear();
      this.closeRequested.emit();
    }
  }
}
